import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const ORANGE = "#f59e0b";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(items) {
  return items[randInt(0, items.length - 1)];
}

function stripTrailingZeros(value) {
  return value.replace(/,?0+$/, "");
}

function formatDecimal(units, decimalPlaces) {
  const raw = String(Math.abs(units)).padStart(decimalPlaces + 1, "0");
  const intPart = raw.slice(0, raw.length - decimalPlaces);
  const fracPart = raw.slice(raw.length - decimalPlaces);
  const sign = units < 0 ? "-" : "";
  if (decimalPlaces === 0) return `${sign}${intPart}`;
  return stripTrailingZeros(`${sign}${intPart},${fracPart}`);
}

function formatMathDecimal(value) {
  return String(value).replace(".", "{,}");
}

function countTrailingZeros(value) {
  let n = value;
  let count = 0;
  while (n !== 0 && n % 10 === 0) {
    count += 1;
    n /= 10;
  }
  return count;
}

function digitsOfNumber(value) {
  return String(value).split("");
}

function pickData(options = {}) {
  const decimalPlacesOptions = options.decimalPlacesOptions || [2, 3];
  const multipliers = options.multipliers;
  const multiplierDecimalPlacesOptions = options.multiplierDecimalPlacesOptions || [0];

  for (let i = 0; i < 600; i += 1) {
    const decimalPlacePair = options.decimalPlacePairs ? pickOne(options.decimalPlacePairs) : null;
    const decimalPlaces = decimalPlacePair ? decimalPlacePair[0] : pickOne(decimalPlacesOptions);
    const multiplierDecimalPlaces = decimalPlacePair ? decimalPlacePair[1] : pickOne(multiplierDecimalPlacesOptions);
    const minUnits = decimalPlaces === 1 ? 12 : decimalPlaces === 3 ? 1010 : 110;
    const maxUnits = decimalPlaces === 1 ? 99 : decimalPlaces === 3 ? 9999 : 999;
    const decimalUnits = randInt(minUnits, maxUnits);
    const multiplier =
      multipliers && multipliers.length
        ? pickOne(multipliers)
        : multiplierDecimalPlaces === 2
        ? randInt(112, 999)
        : multiplierDecimalPlaces === 1
        ? randInt(12, 99)
        : randInt(2, 9);
    if (decimalUnits % 10 === 0) continue;
    if (multiplierDecimalPlaces > 0 && multiplier % 10 === 0) continue;

    const productUnits = decimalUnits * multiplier;
    const resultDecimalPlaces = decimalPlaces + multiplierDecimalPlaces;
    return {
      decimalUnits,
      multiplier,
      productUnits,
      decimalPlaces,
      multiplierDecimalPlaces,
      resultDecimalPlaces,
      decimalText: formatDecimal(decimalUnits, decimalPlaces),
      multiplierText: formatDecimal(multiplier, multiplierDecimalPlaces),
      productText: formatDecimal(productUnits, resultDecimalPlaces),
    };
  }

  return {
    decimalUnits: 317,
    multiplier: 6,
    productUnits: 1902,
    decimalPlaces: 2,
    multiplierDecimalPlaces: 0,
    resultDecimalPlaces: 2,
    decimalText: "3,17",
    multiplierText: "6",
    productText: "19,02",
  };
}

function analyzeMultiplication(decimalUnits, multiplier, resultDecimalPlaces, topDecimalPlaces, multiplierDecimalPlaces = 0) {
  const trailingZeros = multiplierDecimalPlaces === 0 ? countTrailingZeros(multiplier) : 0;
  const effectiveMultiplier = multiplier / 10 ** trailingZeros;
  const topDigits = digitsOfNumber(decimalUnits);
  const multiplierDigits =
    multiplierDecimalPlaces > 0
      ? String(effectiveMultiplier).padStart(multiplierDecimalPlaces + 1, "0").split("")
      : digitsOfNumber(effectiveMultiplier);
  const productDigits = digitsOfNumber(decimalUnits * multiplier);
  const cols = Math.max(topDigits.length, productDigits.length) + 1;
  const topStart = cols - trailingZeros - topDigits.length;
  const bottomStart = cols - trailingZeros - multiplierDigits.length;
  const topCommaAfter = topStart + topDigits.length - topDecimalPlaces - 1;
  const bottomCommaAfter = bottomStart + multiplierDigits.length - multiplierDecimalPlaces - 1;
  const zeroBoundary = trailingZeros > 0 ? cols - trailingZeros : null;

  const top = Array(cols).fill("");
  const bottom = Array(cols).fill("");
  const postponedZeros = Array(cols).fill("");
  const result = Array(cols).fill("");
  const carries = Array(cols).fill("");

  topDigits.forEach((digit, idx) => {
    top[topStart + idx] = digit;
  });
  multiplierDigits.forEach((digit, idx) => {
    bottom[bottomStart + idx] = digit;
  });
  for (let i = 0; i < trailingZeros; i += 1) {
    bottom[cols - trailingZeros + i] = "0";
  }
  const highlightBottom = multiplierDigits.map((_, idx) => bottomStart + idx);
  for (let i = 0; i < trailingZeros; i += 1) {
    postponedZeros[cols - trailingZeros + i] = "0";
  }

  const steps = [];
  const detailLines = [];
  let carry = 0;

  for (let sourceIdx = topDigits.length - 1; sourceIdx >= 0; sourceIdx -= 1) {
    const digit = Number(topDigits[sourceIdx]);
    const gridIdx = topStart + sourceIdx;
    const raw = digit * effectiveMultiplier + carry;
    const writtenDigit = raw % 10;
    const carryOut = Math.floor(raw / 10);
    const isLast = sourceIdx === 0;
    if (isLast && carryOut > 0) {
      const rawDigits = digitsOfNumber(raw);
      rawDigits.forEach((rawDigit, idx) => {
        result[gridIdx - rawDigits.length + 1 + idx] = rawDigit;
      });
    } else {
      result[gridIdx] = String(writtenDigit);
    }

    detailLines.push({
      digit,
      multiplier: effectiveMultiplier,
      carryIn: carry,
      raw,
      writtenDigit,
      carryOut,
      isLast,
    });

    if (carryOut > 0 && !isLast && gridIdx > 0) {
      carries[gridIdx - 1] = String(carryOut);
    }

    steps.push({
      top: [...top],
      bottom: [...bottom],
      postponedZeros: [...postponedZeros],
      result: [...result],
      carries: [...carries],
      highlightTop: [gridIdx],
      highlightBottom,
      highlightResult: isLast && carryOut > 0 ? [gridIdx - 1, gridIdx] : [gridIdx],
      highlightCarry: carryOut > 0 && !isLast && gridIdx > 0 ? [gridIdx - 1] : [],
      decimalPlaces: resultDecimalPlaces,
      topCommaAfter,
      multiplierDecimalPlaces,
      bottomCommaAfter,
      zeroBoundary,
      showDecimalInResult: false,
    });

    carry = carryOut;
  }

  if (trailingZeros > 0) {
    const appendedZeroIndices = [];
    for (let i = 0; i < trailingZeros; i += 1) {
      const idx = cols - trailingZeros + i;
      result[idx] = "0";
      appendedZeroIndices.push(idx);
    }

    steps.push({
      top: [...top],
      bottom: [...bottom],
      postponedZeros: [...postponedZeros],
      result: [...result],
      carries: [...carries],
      highlightTop: [],
      highlightBottom,
      highlightResult: appendedZeroIndices,
      highlightCarry: [],
      decimalPlaces: resultDecimalPlaces,
      topCommaAfter,
      multiplierDecimalPlaces,
      bottomCommaAfter,
      zeroBoundary,
      showDecimalInResult: false,
    });
  }

  steps.push({
    top: [...top],
    bottom: [...bottom],
    postponedZeros: [...postponedZeros],
    result: [...result],
    carries: [...carries],
    highlightTop: [],
    highlightBottom,
    highlightResult: [],
    highlightCarry: [],
    decimalPlaces: resultDecimalPlaces,
    topCommaAfter,
    multiplierDecimalPlaces,
    bottomCommaAfter,
    zeroBoundary,
    showDecimalInResult: true,
  });

  return { detailLines, steps, effectiveMultiplier, trailingZeros };
}

function Row({
  sign,
  digits,
  highlightIndices = [],
  small = false,
  decimalPlaces = 0,
  showDecimal = false,
  commaAfterOverride = null,
  muted = false,
}) {
  const commaAfter = commaAfterOverride == null ? digits.length - decimalPlaces - 1 : commaAfterOverride;
  const fontSize = small ? "0.72em" : "1em";
  const commaFontSize = small ? "0.5em" : "0.74em";
  const digitTrack = "1.08ch";

  return (
    <div
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: `1ch repeat(${digits.length}, ${digitTrack})`,
        columnGap: "0.18em",
        alignItems: "end",
        whiteSpace: "nowrap",
      }}
    >
      <span>{sign || "\u00A0"}</span>
      {digits.map((digit, idx) => {
        const highlighted = highlightIndices.includes(idx);
        const hasComma = showDecimal && decimalPlaces > 0 && idx === commaAfter;
        return (
          <span
            key={`digit-${idx}`}
            style={
              highlighted
                ? {
                    color: ORANGE,
                    fontWeight: 700,
                    fontSize,
                    position: "relative",
                    display: "inline-block",
                    width: "100%",
                    textAlign: "center",
                  }
                : {
                    fontSize,
                    color: muted ? "var(--color-muted, #94a3b8)" : undefined,
                    position: "relative",
                    display: "inline-block",
                    width: "100%",
                    textAlign: "center",
                  }
            }
          >
            {digit === "" ? "\u00A0" : digit}
            {hasComma ? (
              <span
                style={{
                  position: "absolute",
                  right: "-0.62ch",
                  bottom: small ? "-0.02em" : "0.04em",
                  fontSize: commaFontSize,
                  fontWeight: 700,
                  lineHeight: 1,
                  pointerEvents: "none",
                }}
              >
                ,
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

function MiniBoard({ step }) {
  const digitTrack = "1.08ch";
  const columnGap = "0.18em";
  const boundaryLeft =
    step.zeroBoundary == null
      ? null
      : `calc(1ch + ${step.zeroBoundary} * (${digitTrack} + ${columnGap}) + ${columnGap} * 0.35)`;

  return (
    <div
      style={{
        padding: "10px 14px",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "transparent",
        width: "fit-content",
      }}
    >
      <div
        style={{
          position: "relative",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          lineHeight: 1.2,
          display: "inline-block",
        }}
      >
        {boundaryLeft ? (
          <div
            style={{
              position: "absolute",
              top: "1.15em",
              bottom: "0.1em",
              left: boundaryLeft,
              borderLeft: `1px dashed ${ORANGE}`,
              pointerEvents: "none",
            }}
          />
        ) : null}
        <Row
          sign=""
          digits={step.carries}
          highlightIndices={step.highlightCarry}
          small
          decimalPlaces={step.decimalPlaces}
          commaAfterOverride={step.topCommaAfter}
        />
        <Row
          sign=""
          digits={step.top}
          highlightIndices={step.highlightTop}
          decimalPlaces={step.decimalPlaces}
          commaAfterOverride={step.topCommaAfter}
          showDecimal
        />
        <Row
          sign="x"
          digits={step.bottom}
          highlightIndices={step.highlightBottom}
          decimalPlaces={step.multiplierDecimalPlaces}
          commaAfterOverride={step.bottomCommaAfter}
          showDecimal={step.multiplierDecimalPlaces > 0}
        />
        <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)", width: "100%" }} />
        <Row
          sign=""
          digits={step.result}
          highlightIndices={step.highlightResult}
          decimalPlaces={step.decimalPlaces}
          showDecimal={step.showDecimalInResult}
        />
      </div>
    </div>
  );
}

export function generateTask(options) {
  return {
    type: "DecimalMultiplyByOneDigitTask",
    data: pickData(options),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Oblicz sposobem pisemnym:{" "}
        <InlineMath
          math={`${formatMathDecimal(data.decimalText)} \\cdot ${formatMathDecimal(
            data.multiplierText || String(data.multiplier)
          )}`}
        />
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. 19,02 lub 19.02"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || !Number.isFinite(parsed)) return false;
  const resultDecimalPlaces = task.data.resultDecimalPlaces ?? task.data.decimalPlaces;
  return Math.abs(parsed - task.data.productUnits / 10 ** resultDecimalPlaces) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={formatMathDecimal(data.productText)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ data }) {
  const resultDecimalPlaces = data.resultDecimalPlaces ?? data.decimalPlaces;
  const written = analyzeMultiplication(
    data.decimalUnits,
    data.multiplier,
    resultDecimalPlaces,
    data.decimalPlaces,
    data.multiplierDecimalPlaces || 0
  );
  const placesTextByCount = {
    1: "jedno miejsce",
    2: "dwa miejsca",
    3: "trzy miejsca",
    4: "cztery miejsca",
    5: "pięć miejsc",
  };
  const placesText = placesTextByCount[resultDecimalPlaces] || `${resultDecimalPlaces} miejsca`;
  const hasDecimalMultiplier = (data.multiplierDecimalPlaces || 0) > 0;
  const multiplierMath = formatMathDecimal(data.multiplierText || String(data.multiplier));

  return (
    <div style={{ textAlign: "left" }}>
      {written.trailingZeros > 0 ? (
        <p>
          <strong>Krok 1:</strong> Pomijamy na chwilę końcowe zera w mnożniku:{" "}
          <InlineMath math={`${data.multiplier}=${written.effectiveMultiplier}\\cdot ${10 ** written.trailingZeros}`} />.
          Najpierw mnożymy pisemnie{" "}
          <InlineMath math={`${data.decimalUnits}\\cdot ${written.effectiveMultiplier}`} />, a zera dopiszemy na końcu.
        </p>
      ) : hasDecimalMultiplier ? (
        <p>
          <strong>Krok 1:</strong> Mnożymy pisemnie tak, jakby przecinków nie było:{" "}
          <InlineMath math={`${data.decimalUnits}\\cdot ${data.multiplier}`} />.
        </p>
      ) : (
        <p>
          <strong>Krok 1:</strong> Mnożymy pisemnie tak, jakby przecinka nie było:{" "}
          <InlineMath math={`${data.decimalUnits}\\cdot ${data.multiplier}`} />.
        </p>
      )}

      <p>
        <strong>Krok 2:</strong> Liczymy od prawej strony, a przeniesienia dopisujemy nad kolejną kolumną.
      </p>

      <ul style={{ marginTop: 6, marginBottom: 8 }}>
        {written.detailLines.map((line, idx) => (
          <li key={idx}>
            <InlineMath
              math={
                line.carryIn
                  ? `${line.digit}\\cdot ${line.multiplier}+${line.carryIn}=${line.raw}`
                  : `${line.digit}\\cdot ${line.multiplier}=${line.raw}`
              }
            />{" "}
            {line.isLast ? (
              <>
                {"\u2192"} zapisujemy <InlineMath math={`${line.raw}`} />
              </>
            ) : line.carryOut > 0 ? (
              <>
                {"\u2192"} zapisujemy <InlineMath math={`${line.writtenDigit}`} />, przenosimy{" "}
                <InlineMath math={`${line.carryOut}`} />
              </>
            ) : (
              <>
                {"\u2192"} zapisujemy <InlineMath math={`${line.raw}`} />
              </>
            )}
          </li>
        ))}
      </ul>

      {written.trailingZeros > 0 ? (
        <p>
          <strong>Krok 3:</strong> Dopisujemy z prawej strony{" "}
          {written.trailingZeros === 1 ? "jedno odłożone zero" : `${written.trailingZeros} odłożone zera`}.
        </p>
      ) : null}

      <p>
        <strong>Krok {written.trailingZeros > 0 ? "4" : "3"}:</strong>{" "}
        {hasDecimalMultiplier ? (
          <>
            W obu czynnikach razem jest {placesText} po przecinku (
            <InlineMath math={`${formatMathDecimal(data.decimalText)}\\cdot ${multiplierMath}`} />
            ), więc w wyniku odliczamy {placesText} od prawej strony.
          </>
        ) : (
          <>
            W pierwszej liczbie po przecinku są {placesText}, więc w wyniku też odliczamy {placesText} od prawej
            strony.
          </>
        )}
      </p>

      <div
        style={{
          marginTop: 8,
          marginBottom: 10,
          padding: 10,
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          background: "transparent",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          {written.steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <MiniBoard step={step} />
              {idx < written.steps.length - 1 ? (
                <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--color-text)" }}>{"\u2192"}</div>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      </div>

      <p>
        <strong>Wynik:</strong> <InlineMath math={formatMathDecimal(data.productText)} />
      </p>
    </div>
  );
}
